'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { useLocaleRouter } from '@/hooks/useLocaleRouter';
import { Title } from '@/components/ui/Headings';

// Multilingual types
interface TranslatedText {
  en: string;
  de: string;
  uk: string;
}

interface TranslatedArray {
  en: string[];
  de: string[];
  uk: string[];
}

interface TranslatedItinerary {
  en: Array<{ day: number; title: string; description: string }>;
  de: Array<{ day: number; title: string; description: string }>;
  uk: Array<{ day: number; title: string; description: string }>;
}

// Database journey type with multilingual support
interface DatabaseJourney {
  id: number;
  title: TranslatedText;
  subtitle: TranslatedText;
  description: TranslatedText;
  country?: TranslatedText | null;
  image: string;
  imagePosition?: string | null; // CSS object-position value from cover photo record
  duration: string;
  groupSize: string;
  startingPrice: string;
  currency: string;
  guaranteedDeparture?: string | null;
  guaranteedDepartures?: Array<{
    date: string;
    status: string;
    price: string;
    currency: string;
    bookedTravelers?: number;
    maxTravelers?: number;
  }> | null;
  highlights: TranslatedArray;
  overview?: TranslatedText | null;
  whatMakesItSpecial?: TranslatedText | null;
  itinerary?: TranslatedItinerary | null;
  availableDates?: Array<{
    month: string;
    status: string;
    price: string;
  }> | null;
  includes: TranslatedArray;
  notIncluded: TranslatedArray;
  isActive: boolean;
  displayOrder: number | null;
  // Optional metadata fields (not returned by public API to avoid SSR serialization issues)
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string | null;
}

export interface JourneyCardProps {
  journey: DatabaseJourney;
}

const JourneyCard: React.FC<JourneyCardProps> = ({ journey }) => {
  const { i18n, t } = useTranslation();
  const { createLocalizedPath } = useLocaleRouter();
  
  // Get current language (en, de, or uk)
  const currentLang = (i18n.language || 'en') as keyof TranslatedText;

  
  // Get translated content for current language
  const title = journey.title[currentLang] || journey.title.en;
  const country = journey.country?.[currentLang] || journey.country?.en;
  
  // Format price with currency
  const formatPrice = (price: string, currency: string) => {
    const currencyConfig: { [key: string]: { symbol: string; position: 'before' | 'after' } } = {
      'EUR': { symbol: '€', position: 'before' },
      'USD': { symbol: '$', position: 'before' },
      'GBP': { symbol: '£', position: 'before' },
      'JPY': { symbol: '¥', position: 'before' },
      'CAD': { symbol: 'C$', position: 'before' },
      'AUD': { symbol: 'A$', position: 'before' },
      'CHF': { symbol: 'CHF', position: 'after' },
      'NOK': { symbol: 'kr', position: 'after' },
      'SEK': { symbol: 'kr', position: 'after' },
      'DKK': { symbol: 'kr', position: 'after' }
    };
    
    // Default to EUR if currency is not provided
    const currencyCode = currency || 'EUR';
    const config = currencyConfig[currencyCode] || currencyConfig['EUR'];
    
    // Clean the price string to remove any existing currency symbols and commas
    const cleanPrice = price.replace(/[€$£¥C$A$CHFkr,]/g, '').trim();
    const numericPrice = parseFloat(cleanPrice);
    
    if (isNaN(numericPrice)) {
      return price; // Return original if parsing fails
    }
    
    // Format with proper thousands separators
    const formattedPrice = numericPrice.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    // Return formatted price with currency symbol in correct position
    return config.position === 'before' 
      ? `${config.symbol}${formattedPrice}`
      : `${formattedPrice} ${config.symbol}`;
  };
  
  return (
    <Link
      href={createLocalizedPath(`/signature-journeys/${journey.id}`)}
      className="group bg-white rounded-xl overflow-hidden shadow hover:shadow-lg transition-all duration-300 flex flex-col"
    >
      <div className="relative overflow-hidden h-40">
        <Image
          src={journey.image}
          alt={title}
          fill
          className="object-cover transform group-hover:scale-105 transition-transform duration-500"
          style={{
            objectPosition: journey.imagePosition || 'center center'
          }}
        />
        {/* Country Overlay */}
        {country && (
          <div className="absolute top-3 right-3 bg-white px-2.5 py-1 rounded text-sm font-semibold text-gray-800">
            {country}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <Title size="sm" className="leading-snug font-semibold text-primary-600 mb-2 group-hover:text-primary-500 transition-colors">
          {title}
        </Title>
        <div className="flex-grow"></div>
        {(journey.guaranteedDepartures && journey.guaranteedDepartures.length > 0) && (() => {
          // Find the closest departure date
          const sortedDepartures = journey.guaranteedDepartures
            .filter(dep => dep.status.toLowerCase() === 'available')
            .sort((a, b) => {
              const dateA = new Date(a.date);
              const dateB = new Date(b.date);
              return dateA.getTime() - dateB.getTime();
            });
          
          const closestDeparture = sortedDepartures[0];
          
          if (!closestDeparture) return null;
          
          return (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-1.5 text-sm min-w-0 flex-shrink">
                  <svg className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-gray-800 truncate">{closestDeparture.date}</span>
                </div>
                <div className="text-sm font-semibold text-primary-700 flex-shrink-0 whitespace-nowrap">
                  <span className="text-xs text-gray-600 font-normal">{t('home.journeyCard.startsFrom')} </span>
                  {formatPrice(closestDeparture.price, closestDeparture.currency)}
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Fallback to legacy guaranteedDeparture field */}
        {(!journey.guaranteedDepartures || journey.guaranteedDepartures.length === 0) && journey.guaranteedDeparture && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-700 min-w-0 flex-shrink">
                <svg className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium truncate">{journey.guaranteedDeparture}</span>
              </div>
              <div className="text-sm font-semibold text-primary-700 flex-shrink-0 whitespace-nowrap">
                <span className="text-xs text-gray-600 font-normal">{t('home.journeyCard.startsFrom')} </span>
                {formatPrice(journey.startingPrice, journey.currency)}
              </div>
            </div>
          </div>
        )}
        
        {/* Fallback to show startingPrice when no departures are available */}
        {(!journey.guaranteedDepartures || journey.guaranteedDepartures.length === 0) && 
         !journey.guaranteedDeparture && 
         journey.startingPrice && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-end">
              <div className="text-sm font-semibold text-primary-700 flex-shrink-0 whitespace-nowrap">
                <span className="text-xs text-gray-600 font-normal">{t('home.journeyCard.startsFrom')} </span>
                {formatPrice(journey.startingPrice, journey.currency)}
              </div>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

export default JourneyCard;


